import React, {useState} from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  TextField,
  Typography,
} from '@mui/material';
import {
  useGetProductionSetInfo,
  useGetProductionComponents,
  useGetTemplateComponents,
} from '../../../hooks/productions/useProductions';
import {
  GetProductionSetInfoRequestParams,
  GetProductionComponentsRequestParam,
  GetTemplateComponentsRequestParam,
} from '../../../api/types/production.type';
import {ReactComponent as CaretDown} from '../../../assets/images/caret-down.svg';

const ProductionSetInfoApiTest: React.FC = () => {
  const [securityGrpId, setSecurityGrpId] = useState<number>(1);
  const [setId, setSetId] = useState<number>(1);
  const [selectedView, setSelectedView] = useState<string>('Tile');
  const [moduleType, setModuleType] = useState<string>('production');
  const [projectId, setProjectId] = useState<number>(1);
  const [templateId, setTemplateId] = useState<number>(5);
  const [copySuccess, setCopySuccess] = useState<boolean>(false);
  const [isEnabled, setIsEnabled] = useState<boolean>(false);
  const [isTemplateEnabled, setIsTemplateEnabled] = useState<boolean>(false);

  const params: GetProductionSetInfoRequestParams = {
    securityGrpId,
    setId,
    selectedView,
  };

  const {
    data,
    isPending,
    error,
    refetch: refetchSetInfo,
  } = useGetProductionSetInfo(params, isEnabled);

  const productionComponentsParams: GetProductionComponentsRequestParam = {
    SecurityGroupId: securityGrpId,
    moduletype: moduleType,
    parentsetid: setId,
    productionId: 0,
    exportProductionId: 0,
  };

  const {
    data: productionComponents,
    isPending: isComponentsPending,
    error: componentsError,
    refetch: refetchComponents,
  } = useGetProductionComponents(productionComponentsParams, isEnabled);

  const templateComponentsParams: GetTemplateComponentsRequestParam = {
    SecurityGroupIdForProduction: projectId,
    TemplateId: templateId,
  };

  const {
    data: templateComponents,
    isPending: isTemplatePending,
    error: templateError,
    refetch: refetchTemplateComponents,
  } = useGetTemplateComponents(templateComponentsParams, isTemplateEnabled);

  const handleFetch = () => {
    if (!isEnabled) {
      setIsEnabled(true);
    } else {
      refetchSetInfo();
      refetchComponents();
    }
  };

  const handleFetchTemplate = () => {
    if (!isTemplateEnabled) {
      setIsTemplateEnabled(true);
    } else {
      refetchTemplateComponents();
    }
  };

  const handleReset = () => {
    setSecurityGrpId(1);
    setSetId(1);
    setSelectedView('Tile');
    setModuleType('production');
    setProjectId(1);
    setTemplateId(5);
    setCopySuccess(false);
    setIsTemplateEnabled(false);
  };

  const copyToClipboard = async (payload: unknown) => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleCopyPayload = () =>
    copyToClipboard({
      productionSetInfoPayload: params,
      productionComponentsPayload: productionComponentsParams,
      templateComponentsPayload: templateComponentsParams,
    });

  const handleCopyResponse = () =>
    copyToClipboard({
      productionSetInfo: data ?? null,
      productionComponents: productionComponents ?? null,
      templateComponents: templateComponents ?? null,
    });

  return (
    <Box className="demo-page" padding={3}>
      <Typography
        variant="h4"
        className="typo-text headline2_size"
        gutterBottom
      >
        🧪 Production Set Info API Test
      </Typography>
      <Typography
        className="typo-text body3_size"
        sx={{color: '#666', marginBottom: 2}}
      >
        Retrieve metadata and statistics for a specific production/export set,
        including component details and template component configuration.
      </Typography>

      <Box
        sx={{
          backgroundColor: '#f5f5f5',
          padding: 2,
          borderRadius: 1,
          marginBottom: 3,
        }}
      >
        <Typography variant="h6" className="typo-text subhead2_size">
          Request Parameters
        </Typography>

        <Box sx={{display: 'flex', gap: 2, marginTop: 2, flexWrap: 'wrap'}}>
          <TextField
            label="Security Group ID"
            type="number"
            value={securityGrpId}
            onChange={(e) => setSecurityGrpId(Number(e.target.value))}
            size="small"
            placeholder="Enter security group ID"
          />
          <TextField
            label="Production Set ID"
            type="number"
            value={setId}
            onChange={(e) => setSetId(Number(e.target.value))}
            size="small"
            placeholder="Enter production set ID"
          />
          <TextField
            label="Selected View"
            type="text"
            value={selectedView}
            onChange={(e) => setSelectedView(e.target.value)}
            size="small"
            placeholder="Tile / List / etc."
          />
          <TextField
            label="Module Type"
            type="text"
            value={moduleType}
            onChange={(e) => setModuleType(e.target.value)}
            size="small"
            placeholder="production / export"
          />
          <TextField
            label="Template Project ID"
            type="number"
            value={projectId}
            onChange={(e) => setProjectId(Number(e.target.value))}
            size="small"
            placeholder="Enter project ID"
          />
          <TextField
            label="Template ID"
            type="number"
            value={templateId}
            onChange={(e) => setTemplateId(Number(e.target.value))}
            size="small"
            placeholder="Enter template ID"
          />
        </Box>

        <Box sx={{marginTop: 2, display: 'flex', gap: 2, flexWrap: 'wrap'}}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleFetch}
            disabled={isPending || isComponentsPending}
          >
            {isPending || isComponentsPending
              ? '⏳ Fetching...'
              : '🚀 Fetch Set Info'}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleFetchTemplate}
            disabled={isTemplatePending}
          >
            {isTemplatePending
              ? '⏳ Fetching Template...'
              : '🚀 Fetch Template Components'}
          </Button>
          <Button variant="outlined" onClick={handleReset}>
            🔄 Reset
          </Button>
          <Button variant="outlined" onClick={handleCopyPayload}>
            📋 Copy Payload
          </Button>
          <Button
            variant="outlined"
            onClick={handleCopyResponse}
            disabled={!data && !productionComponents && !templateComponents}
          >
            📋 Copy Response
          </Button>
        </Box>

        {copySuccess && (
          <Typography sx={{marginTop: 1}} className="typo-text caption1_size">
            ✅ Copied to clipboard!
          </Typography>
        )}
      </Box>

      {(isPending || isComponentsPending) && (
        <Box
          sx={{
            padding: 3,
            backgroundColor: '#e3f2fd',
            borderRadius: 1,
            border: '1px solid #2196f3',
          }}
        >
          <Typography className="typo-text body2_size">
            ⏳ Loading production set information...
          </Typography>
        </Box>
      )}

      {(error || componentsError) && (
        <Box
          sx={{
            padding: 3,
            backgroundColor: '#ffebee',
            borderRadius: 1,
            border: '1px solid #f44336',
          }}
        >
          <Typography className="typo-text subhead3_size" color="error">
            ❌ Error
          </Typography>
          <Typography className="typo-text body3_size" color="error">
            {error?.message || componentsError?.message}
          </Typography>
        </Box>
      )}

      {(data || productionComponents) && !isPending && !isComponentsPending && (
        <Accordion
          defaultExpanded
          sx={{
            marginTop: 3,
            boxShadow: 2,
            borderRadius: 1,
            '&:before': {display: 'none'},
          }}
        >
          <AccordionSummary
            expandIcon={<CaretDown />}
            sx={{
              backgroundColor: '#f0f7ff',
              borderBottom: '1px solid #e0e0e0',
            }}
          >
            <Typography className="typo-text subhead2_size">
              ✅ Production Set & Components Payload
            </Typography>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              backgroundColor: '#fafafa',
              borderRadius: '0 0 8px 8px',
            }}
          >
            <pre
              style={{
                backgroundColor: '#1e1e1e',
                color: '#d4d4d4',
                padding: '16px',
                borderRadius: '8px',
                overflowX: 'auto',
              }}
            >
              {JSON.stringify(
                {
                  productionSetInfo: data ?? null,
                  productionComponents: productionComponents ?? null,
                },
                null,
                2
              )}
            </pre>
          </AccordionDetails>
        </Accordion>
      )}

      {templateComponents && !isTemplatePending && (
        <Accordion
          sx={{
            marginTop: 3,
            boxShadow: 2,
            borderRadius: 1,
            '&:before': {display: 'none'},
          }}
        >
          <AccordionSummary
            expandIcon={<CaretDown />}
            sx={{
              backgroundColor: '#f0f7ff',
              borderBottom: '1px solid #e0e0e0',
            }}
          >
            <Typography className="typo-text subhead2_size">
              📦 Template Components Payload
            </Typography>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              backgroundColor: '#fafafa',
              borderRadius: '0 0 8px 8px',
            }}
          >
            <pre
              style={{
                backgroundColor: '#1e1e1e',
                color: '#d4d4d4',
                padding: '16px',
                borderRadius: '8px',
                overflowX: 'auto',
              }}
            >
              {JSON.stringify(templateComponents, null, 2)}
            </pre>
          </AccordionDetails>
        </Accordion>
      )}

      {templateError && (
        <Box
          sx={{
            padding: 3,
            backgroundColor: '#ffebee',
            borderRadius: 1,
            border: '1px solid #f44336',
            marginTop: 3,
          }}
        >
          <Typography className="typo-text subhead3_size" color="error">
            ❌ Template Error
          </Typography>
          <Typography className="typo-text body3_size" color="error">
            {templateError.message}
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          marginTop: 4,
          padding: 3,
          backgroundColor: '#f5f5f5',
          borderRadius: 1,
        }}
      >
        <Typography className="typo-text subhead2_size" gutterBottom>
          API Details
        </Typography>
        <Typography className="typo-text body3_size">
          Endpoint:{' '}
          <Typography component="span" className="typo-text body3_size">
            /productions/api/v1/production/view/renderproductionsetinfo
          </Typography>
        </Typography>
        <Typography className="typo-text body3_size">
          Template Endpoint:{' '}
          <Typography component="span" className="typo-text body3_size">
            /productions/api/v1/production/templates/components-details
          </Typography>
        </Typography>
        <Typography className="typo-text body3_size">
          Method:{' '}
          <Typography component="span" className="typo-text body3_size">
            GET
          </Typography>
        </Typography>
      </Box>
    </Box>
  );
};

export default ProductionSetInfoApiTest;
